import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { errMsg } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      nav("/");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-[#0B0F17]">
      <div className="hidden lg:flex w-[46%] flex-col justify-between bg-slate-900 p-12 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1758369637013-352ed7636c33?crop=entropy&cs=srgb&fm=jpg&q=85)", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="relative">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500 font-display font-extrabold text-lg text-slate-900">K</span>
            <div>
              <p className="font-display font-bold text-lg leading-tight">Krishna Group</p>
              <p className="text-[11px] uppercase tracking-widest text-slate-400">Business Management System</p>
            </div>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-display text-4xl font-extrabold tracking-tight leading-tight">
            One login.<br />Every company.<br />Every rupee accounted.
          </h1>
          <p className="mt-4 max-w-md text-sm text-slate-300 leading-relaxed">
            Billing with GST, payments & reconciliation, inventory, ledgers and learning —
            across Krishna Architectural Works, Krishna Fittings and Krishna Treats.
          </p>
          <div className="mt-6 flex gap-2">
            {["KAWSP", "KF", "KT"].map((c, i) => (
              <span key={c} className="rounded-md border px-2.5 py-1 text-xs font-mono font-semibold"
                style={{ borderColor: ["#D97706", "#2563EB", "#059669"][i], color: ["#F59E0B", "#60A5FA", "#34D399"][i] }}>{c}</span>
            ))}
          </div>
        </div>
        <p className="relative text-xs text-slate-500">© 2026 Krishna Group. All rights reserved.</p>
      </div>

      <div className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <h2 className="font-display text-2xl font-bold tracking-tight" data-testid="login-title">Sign in</h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Access your authorized companies</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" required autoComplete="email"
                value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@krishnagroup.com" className="mt-1.5 h-11" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" data-testid="forgot-password-link"
                  className="text-xs font-medium text-amber-600 hover:text-amber-700 dark:text-amber-400">Forgot password?</Link>
              </div>
              <Input id="password" data-testid="login-password" type="password" required autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5 h-11" />
            </div>
            <Button data-testid="login-submit-btn" type="submit" disabled={busy}
              className="h-11 w-full bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 font-semibold">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Sign in
            </Button>
          </form>
          <p className="mt-6 text-center text-xs text-slate-400">
            Protected by role-based access · Company-isolated data
          </p>
          {/* Initial administrator setup link intentionally hidden for now. The /setup route remains available. */}
        </div>
      </div>
    </div>
  );
}
